import * as React from 'react';
import { FastField } from 'formik';
import { FormGroup, Button, RotateLoader, Switch, Input } from '@lib/components';
import { MobxComponent } from '../../../../../../../mobx/components/index';
import { inject, observer } from 'mobx-react';
import { RestaurantForm } from '../../../../../../../mobx/components/restaurant-form';
import { UI } from '../../../../../../../core/ui';
import { PaymentBaseFields } from './base';
import styled from 'styled-components';

const ConnectToStripeButton = styled.button`
display: flex;
padding: 12px 32px;
justify-content: center;
align-items: center;
gap: 8px;
border-radius: 4px;
background: var(--Primary-Red-Orange-1, #FB2F2F);
color: var(--Neutral-Gray, #F1F4F7);
border: 1px solid var(--Primary-Red-Orange-1, #FB2F2F);
text-align: center;
font-family: Inter;
font-size: 14px;
font-style: normal;
font-weight: 600;
line-height: 16px; /* 114.286% */
cursor: pointer;
&:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
`
const AdvanceText = styled.span`
color: var(--Primary-Red-Orange-1, #FB2F2F);
font-size: 14px;
font-style: normal;
font-weight: 500;
line-height: 24px; /* 171.429% */
text-decoration-line: underline;
text-decoration-style: solid;
text-decoration-skip-ink: auto;
text-decoration-thickness: auto;
text-underline-offset: auto;
text-underline-position: from-font;
`

interface Props {}
interface State {}
type FormValues = T.Schema.Restaurant.Payments.RestaurantPaymentNuvei;

@inject('store')
@observer
export class SettingsFormPaymentsNuvei extends MobxComponent<Props, State> {
  initialValues: FormValues;

  constructor(props: Props) {
    super(props);
    this.state = {};
    const r = this.injected.store.restaurant!;
    this.initialValues = r.settings.payments.nuvei || {
      enabled: false,
      nuvei_sandbox: true,
      label: '',
      services: [],
      api_key: '',
      api_secret: '',
      user_name: '',
      password: '',
      public_key: '',
      min_order: 0,
    };
  }

  render() {
    const { restaurant } = this.injected.store;

    return (
      <RestaurantForm<FormValues>
        submit={async (r, values) => {
          r.settings.payments.nuvei = {...values};
          const update = { $set: { 'settings.payments.nuvei': r.settings.payments.nuvei } };
          // this.checkAndRegisterDomain();
          return { r, update };
        }}
        initialValues={this.initialValues}
        onError={() => UI.notification.error('An error occurred')}
        onSuccessMessage="Settings Updated"
      >
        {({ form, error, getFieldError }) => {
          const { isSubmitting, setFieldValue } = form;
          return (
            <div className="p-4">
              <FastField
                name="enabled"
                render={({ field }: any) => (
                  <FormGroup title="Enabled" help="Allows customers to pay using this method">
                    <Switch
                      id="enable-switch"
                      checked={field.value}
                      onChange={e => setFieldValue('enabled', e.target.checked)}
                    />
                  </FormGroup>
                )}
              />

              <FormGroup>
                <div>
                  <div style={{ marginBottom: '10px' }}>
                    <p
                      style={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: 'red',
                        marginBottom: '10px',
                      }}
                    >
                      IMPORTANT
                    </p>
                    <p style={{ fontSize: '16px', lineHeight: '18px' }}>
                      Make sure to set the test/live setting in the Nuvei settings area to match what you have
                      here.
                    </p>
                  </div>
                </div>
              </FormGroup>

              <FastField
                name="nuvei_sandbox"
                render={({ field }: any) => (
                  <FormGroup
                    title="Sandbox Mode"
                    help="Enable sandbox mode for testing purposes"
                  >
                    <Switch
                      id="nuvei-sandbox-switch"
                      checked={field.value}
                      onChange={e => setFieldValue('nuvei_sandbox', e.target.checked)}
                    />
                  </FormGroup>
                )}
              />

              <FastField
                name="api_key"
                render={({ field }: any) => (
                  <React.Fragment>
                    <FormGroup
                      title="Nuvei API Key"
                      help="Login to tillpayment gateway -> Connectors -> API Key"
                    >
                      <Input type="text" {...field} autoSave="false" autoCorrect="false" />
                    </FormGroup>
                  </React.Fragment>
                )}
              />

              <FastField
                name="api_secret"
                render={({ field }: any) => (
                  <React.Fragment>
                    <FormGroup
                      title="Nuvei Shared Secret"
                      help="Login to tillpayment gateway -> Connectors -> Shared Secret"
                    >
                      <Input type="text" {...field} autoSave="false" autoCorrect="false" />
                    </FormGroup>
                  </React.Fragment>
                )}
              />

              <FastField
                name="public_key"
                render={({ field }: any) => (
                  <React.Fragment>
                    <FormGroup
                      title="Nuvei API Public Key"
                      help="Login to tillpayment gateway -> Connectors -> Public Integration Key"
                    >
                      <Input type="text" {...field} autoSave="false" autoCorrect="false" />
                    </FormGroup>
                  </React.Fragment>
                )}
              />

              <FastField
                name="user_name"
                render={({ field }: any) => (
                  <React.Fragment>
                    <FormGroup
                      title="Nuvei Account User Name"
                    >
                      <Input type="text" {...field} autoSave="false" autoCorrect="false" />
                    </FormGroup>
                  </React.Fragment>
                )}
              />

              <FastField
                name="password"
                render={({ field }: any) => (
                  <React.Fragment>
                    <FormGroup
                      title="Nuvei Account Password"
                    >
                      <Input type="text" {...field} autoSave="false" autoCorrect="false" />
                    </FormGroup>
                  </React.Fragment>
                )}
              />

              <PaymentBaseFields defaultLabel="Nuvei" setFieldValue={setFieldValue} />

              {error && <FormGroup error={error} />}

              <Button full={true} color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting && <RotateLoader size={2} color="white" />}
                {!isSubmitting && 'Save'}
              </Button>
            </div>
          );
        }}
      </RestaurantForm>
    );
  }
}
